import type { JSX } from "react";
import { useEffect, useRef } from "react";

const TILE = 160;
const FRAMES = 4;
const FPS = 12;

/**
 * Film-grain overlay, redrawn at ~12fps — film-gate flicker, not smooth shader
 * drift. Pre-rendered noise tiles swapped as a canvas pattern; paused entirely
 * for reduced-motion users (one static frame).
 *
 * The run gate is the same lifecycle StarField uses: one owner for "hidden
 * document" and "reduced motion", so a background/restore cycle or a live
 * preference flip starts and stops the flicker exactly once. The last painted
 * frame stays on screen in every state — this is a stop, never a hide.
 */
export function Grain(): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    const tiles: HTMLCanvasElement[] = [];
    for (let f = 0; f < FRAMES; f++) {
      const tile = document.createElement("canvas");
      tile.width = TILE;
      tile.height = TILE;
      const tctx = tile.getContext("2d");
      if (!tctx) continue;
      const img = tctx.createImageData(TILE, TILE);
      for (let i = 0; i < img.data.length; i += 4) {
        const v = Math.random() * 255;
        img.data[i] = v;
        img.data[i + 1] = v;
        img.data[i + 2] = v;
        img.data[i + 3] = Math.random() * 46; // sparse, faint speckle
      }
      tctx.putImageData(img, 0, 0);
      tiles.push(tile);
    }
    if (tiles.length === 0) return undefined;

    const resize = (): void => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let frame = 0;
    const paint = (): void => {
      const pattern = ctx.createPattern(tiles[frame % tiles.length], "repeat");
      if (pattern) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = pattern;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
      frame++;
    };

    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    // The stop closure doubles as the "is it running" flag, so the gate cannot
    // disagree with the timer it owns.
    let stopTimer: (() => void) | null = null;

    const sync = (): void => {
      const shouldRun = !document.hidden && !reducedQuery.matches;
      if (shouldRun === (stopTimer !== null)) return;
      if (!shouldRun) {
        stopTimer?.();
        stopTimer = null;
        return;
      }
      const id = setInterval(paint, 1000 / FPS);
      stopTimer = () => clearInterval(id);
    };

    paint();
    sync();
    document.addEventListener("visibilitychange", sync);
    reducedQuery.addEventListener("change", sync);
    return () => {
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", sync);
      reducedQuery.removeEventListener("change", sync);
      stopTimer?.();
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-40 h-full w-full opacity-60 mix-blend-overlay"
    />
  );
}
